import React, { useCallback, useEffect, useRef, useState } from 'react';
import Papa from 'papaparse';
import './Evento.css';
import eventoVideo from '../assets/evento.mp4';
import vinosCatalogCsv from '../assets/vinos.csv';
import fichasVinosCsv from '../assets/fichas_vinos_evento.csv';

type Vino = {
  id_vino: number;
  nombre: string;
  sku?: string;
  imagenUrl?: string | null;
  ficha?: FichaVino;
  puntuacion: number;
  nota: string;
};

type CatalogoCsvRow = {
  nombre?: string;
  sku?: string;
};

type CatalogoVino = {
  nombre: string;
  sku: string;
};

type FichaCsvRow = {
  sku?: string;
  nariz?: string;
  boca?: string;
  sensacion?: string;
  maridaje?: string;
  origen?: string;
  tipo?: string;
};

type FichaVino = {
  nariz: string;
  boca: string;
  sensacion: string;
  maridaje: string;
  origen: string;
  tipo: string;
};
const CURRENT_EVENT_NUMBER = 2;
const DEFAULT_VINOS_COUNT = 32;
const VINO_STORAGE_KEY = 'evento_vinos_v1';
const USER_STORAGE_KEY = 'evento_user_v1';
const USERNAME_STORAGE_KEY = 'evento_username_v1';
const TOKEN_STORAGE_KEY = 'evento_token_v1';
const UNLOCK_STORAGE_KEY = 'evento_unlock_v1';
const REVIEW_DEBOUNCE_MS = 1500;
const SESSION_CHANGE_EVENT = 'evento:session-change';
const ACCESS_KEYPAD_DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];
const PRELOAD_BATCH_SIZE = 4;
const preloadedImageUrls = new Set<string>();

const normalizeUserKey = (value: string) => value.trim().toLowerCase();

const buildScopedKey = (baseKey: string, userEmail?: string) => {
  if (!userEmail) {
    return null;
  }
  return `${baseKey}__${encodeURIComponent(userEmail)}`;
};
// const API_BASE_URL = 'https://api.jgwineclub.com';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'https://api.jgwineclub.com';
const LOGIN_ENDPOINT = `${API_BASE_URL}/login`;
const REVIEW_ENDPOINT = `${API_BASE_URL}/review`;

type JwtPayload = {
  exp?: number;
  email?: string;
};

const decodeJwtPayload = (token: string): JwtPayload | null => {
  if (typeof window === 'undefined' || typeof window.atob !== 'function') {
    return null;
  }
  try {
    const parts = token.split('.');
    if (parts.length < 2) {
      return null;
    }
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    let padded = base64;
    while (padded.length % 4 !== 0) {
      padded += '=';
    }
    const json = window.atob(padded);
    return JSON.parse(json) as JwtPayload;
  } catch (error) {
    console.warn('No se pudo decodificar el token JWT', error);
    return null;
  }
};

const isTokenValid = (token: string): boolean => {
  const payload = decodeJwtPayload(token);
  if (!payload || !payload.exp) {
    return true;
  }
  const expiry = payload.exp * 1000;
  return expiry > Date.now();
};

type LoginResponse = {
  token?: string;
  user_id?: number;
  codigo?: string;
  username?: string;
  error?: string;
};

// Precarga ligera para mantener el carrusel utilizable antes de leer el CSV real
const DEFAULT_VINOS: Vino[] = Array.from({ length: DEFAULT_VINOS_COUNT }, (_, idx) => ({
  id_vino: idx + 1,
  nombre: `Vino ${idx + 1}`,
  sku: '',
  imagenUrl: null,
  ficha: undefined,
  puntuacion: 0,
  nota: '',
}));

const VINO_IMAGES: Record<string, string> = (() => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const context = (require as any).context('../assets/vinos', false, /\.(png|jpe?g|svg)$/);
    const images: Record<string, string> = {};
    context.keys().forEach((key: string) => {
      const cleaned = key.replace('./', '');
      const baseName = cleaned.replace(/\.[^.]+$/, '');
      images[baseName] = context(key);
    });
    return images;
  } catch (error) {
    console.warn('No se pudieron precargar las imágenes de vinos', error);
    return {};
  }
})();

const getWaveColors = (tipo?: string) => {
  const normalized = (tipo ?? '').toLowerCase();
  switch (normalized) {
    case 'tinto':
      return {
        start: 'rgba(126, 76, 154, 0.85)',
        mid: 'rgba(85, 46, 119, 0.55)',
        end: 'rgba(85, 46, 119, 0)',
      };
    case 'naranjo':
      return {
        start: 'rgba(244, 171, 89, 0.9)',
        mid: 'rgba(217, 128, 52, 0.7)',
        end: 'rgba(217, 128, 52, 0)',
      };
    case 'rosado':
      return {
        start: 'rgba(255, 191, 210, 0.9)',
        mid: 'rgba(255, 147, 178, 0.65)',
        end: 'rgba(255, 147, 178, 0)',
      };
    case 'blanco':
    default:
      return {
        start: 'rgba(255, 244, 200, 0.82)',
        mid: 'rgba(255, 214, 140, 0.65)',
        end: 'rgba(0, 0, 0, 0)',
      };
  }
};

const parseCatalogo = (csvText: string): CatalogoVino[] => {
  const result = Papa.parse<CatalogoCsvRow>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header: string) => header.trim().toLowerCase(),
  });

  return result.data
    .map((row) => ({
      nombre: (row.nombre ?? '').trim(),
      sku: (row.sku ?? '').trim(),
    }))
    .filter((row): row is CatalogoVino => Boolean(row.nombre) && Boolean(row.sku));
};

const parseFichas = (csvText: string): Record<string, FichaVino> => {
  const result = Papa.parse<FichaCsvRow>(csvText, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (header: string) => header.trim().toLowerCase(),
  });

  return result.data.reduce<Record<string, FichaVino>>((acc, row) => {
    const sku = (row.sku ?? '').trim();
    if (!sku) {
      return acc;
    }
    acc[sku] = {
      nariz: (row.nariz ?? '').trim(),
      boca: (row.boca ?? '').trim(),
      sensacion: (row.sensacion ?? '').trim(),
      maridaje: (row.maridaje ?? '').trim(),
      origen: (row.origen ?? '').trim(),
      tipo: (row.tipo ?? '').trim(),
    };
    return acc;
  }, {});
};

const preloadImage = (url?: string | null) => {
  if (!url || preloadedImageUrls.has(url) || typeof window === 'undefined') {
    return;
  }
  preloadedImageUrls.add(url);
  const image = new Image();
  image.decoding = 'async';
  image.src = url;
};

const Evento: React.FC = () => {
  const [token, setToken] = useState<string | null>(() => {
    if (typeof window === 'undefined') {
      return null;
    }
    const storedToken = window.localStorage.getItem(TOKEN_STORAGE_KEY);
    if (!storedToken) {
      return null;
    }
    if (!isTokenValid(storedToken)) {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      return null;
    }
    return storedToken;
  });
  const getStoredUserRaw = () => {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.localStorage.getItem(USER_STORAGE_KEY) ?? '';
  };
  const getStoredUsername = () => {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.localStorage.getItem(USERNAME_STORAGE_KEY) ?? '';
  };

  const [userKey, setUserKey] = useState<string>(() => normalizeUserKey(getStoredUserRaw()));
  const [username, setUsername] = useState<string>(getStoredUsername);
  const [codigo, setCodigo] = useState<string>('');
  const [codigoError, setCodigoError] = useState<string>('');
  const [loginError, setLoginError] = useState<string>('');
  const [showExplosion, setShowExplosion] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const loadStoredVinos = useCallback((targetEmail: string) => {
    const fallback = DEFAULT_VINOS.map((vino) => ({ ...vino }));
    if (typeof window === 'undefined' || !targetEmail) {
      return fallback;
    }
    const scopedKey = buildScopedKey(VINO_STORAGE_KEY, targetEmail);
    const legacyKey = VINO_STORAGE_KEY;
    try {
      if (scopedKey) {
        const stored = window.localStorage.getItem(scopedKey);
        if (stored) {
          const parsed = JSON.parse(stored) as Vino[];
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed;
          }
        }
      }
      const legacyStored = window.localStorage.getItem(legacyKey);
      if (legacyStored) {
        const parsedLegacy = JSON.parse(legacyStored) as Vino[];
        if (Array.isArray(parsedLegacy) && parsedLegacy.length > 0) {
          if (scopedKey) {
            window.localStorage.setItem(scopedKey, legacyStored);
            window.localStorage.removeItem(legacyKey);
          }
          return parsedLegacy;
        }
      }
    } catch (error) {
      console.warn('No se pudo leer el almacenamiento local de vinos', error);
    }
    return fallback;
  }, []);

  const loadStoredUnlocks = useCallback((targetEmail: string) => {
    if (typeof window === 'undefined' || !targetEmail) {
      return {} as Record<string, boolean>;
    }
    const scopedKey = buildScopedKey(UNLOCK_STORAGE_KEY, targetEmail);
    try {
      if (scopedKey) {
        const stored = window.localStorage.getItem(scopedKey);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed && typeof parsed === 'object') {
            return parsed as Record<string, boolean>;
          }
        }
      }
      const legacyStored = window.localStorage.getItem(UNLOCK_STORAGE_KEY);
      if (legacyStored) {
        const parsedLegacy = JSON.parse(legacyStored);
        if (parsedLegacy && typeof parsedLegacy === 'object') {
          if (scopedKey) {
            window.localStorage.setItem(scopedKey, legacyStored);
            window.localStorage.removeItem(UNLOCK_STORAGE_KEY);
          }
          return parsedLegacy as Record<string, boolean>;
        }
      }
    } catch (error) {
      console.warn('No se pudo leer el estado de desbloqueo', error);
    }
    return {} as Record<string, boolean>;
  }, []);

  const [vinos, setVinos] = useState<Vino[]>(() => loadStoredVinos(userKey));
  const [unlockedVinos, setUnlockedVinos] = useState<Record<string, boolean>>(() => loadStoredUnlocks(userKey));

  const carouselRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const scrollTimeoutRef = useRef<number | null>(null);
  const selectionSourceRef = useRef<'auto' | 'manual'>('auto');
  const sliderTrackRef = useRef<HTMLDivElement | null>(null);
  const sliderHandleRef = useRef<HTMLButtonElement | null>(null);
  const sliderDragRef = useRef({
    active: false,
    pointerId: null as number | null,
    startX: 0,
    startProgress: 0,
  });
  const explosionTimeoutRef = useRef<number | null>(null);
  const vinosRef = useRef<Vino[]>(vinos);
  const lastReviewPayloadRef = useRef<Record<number, { score?: number; nota_cata?: string }>>({});
  const previousSeleccionadoRef = useRef<number>(0);
  const videoLoginRef = useRef<HTMLVideoElement>(null);
  const notaTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [seleccionado, setSeleccionado] = useState(0);
  const [sliderProgress, setSliderProgress] = useState(0);
  const [countdown, setCountdown] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  const vinoActual = vinos[seleccionado] ?? DEFAULT_VINOS[0];

useEffect(() => {
  vinosRef.current = vinos;
}, [vinos]);

  useEffect(() => {
    Object.values(reviewTimeoutsRef.current).forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    reviewTimeoutsRef.current = {};
    lastReviewPayloadRef.current = {};
  }, [userKey]);

// Inicializar video de fondo (mismo enfoque que sección QuienesSomos)
useEffect(() => {
  const video = videoLoginRef.current;
  if (!video) {
    return;
  }

  const attemptPlay = () => {
    const playPromise = video.play();
    if (playPromise) {
      playPromise.catch(() => {
        // El navegador bloqueó el autoplay; intentaremos nuevamente o esperaremos interacción.
      });
    }
  };

  video.defaultMuted = true;
  video.muted = true;
  video.loop = true;
  video.playsInline = true;
  video.playbackRate = 0.75;
  video.setAttribute('playsinline', 'true');
  video.setAttribute('webkit-playsinline', 'true');
  video.setAttribute('muted', 'true');
  video.setAttribute('autoplay', 'true');
  video.setAttribute('loop', 'true');
  video.load();

  attemptPlay();

  const handleLoadedData = () => {
    if (video.paused) {
      attemptPlay();
    }
  };

  const retryInterval = window.setInterval(() => {
    if (!video.paused || video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) {
      window.clearInterval(retryInterval);
      return;
    }
    attemptPlay();
  }, 1500);

  video.addEventListener('loadeddata', handleLoadedData);
  video.addEventListener('canplay', handleLoadedData);
  video.addEventListener('canplaythrough', handleLoadedData);

  const handleUserGesture = () => {
    attemptPlay();
    document.removeEventListener('touchstart', handleUserGesture);
    document.removeEventListener('click', handleUserGesture);
  };

  document.addEventListener('touchstart', handleUserGesture, { passive: true });
  document.addEventListener('click', handleUserGesture, { passive: false });

  return () => {
    window.clearInterval(retryInterval);
    video.removeEventListener('loadeddata', handleLoadedData);
    video.removeEventListener('canplay', handleLoadedData);
    video.removeEventListener('canplaythrough', handleLoadedData);
    document.removeEventListener('touchstart', handleUserGesture);
    document.removeEventListener('click', handleUserGesture);
  };
}, []);

// Contador regresivo hasta el viernes 26 de junio 2026 20:30 UTC-3
useEffect(() => {
  const targetDate = new Date('2026-06-26T20:30:00-03:00').getTime();
  
  const updateCountdown = () => {
    const now = new Date().getTime();
    const difference = targetDate - now;
    
    if (difference > 0) {
      setCountdown({
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
        minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
        seconds: Math.floor((difference % (1000 * 60)) / 1000)
      });
    } else {
      setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    }
  };
  
  updateCountdown();
  const interval = setInterval(updateCountdown, 1000);
  
  return () => clearInterval(interval);
}, []);

useEffect(() => () => {
  if (explosionTimeoutRef.current !== null) {
    window.clearTimeout(explosionTimeoutRef.current);
  }
}, []);

useEffect(() => {
  if (typeof window === 'undefined') {
    return;
  }
  if (token) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
    lastReviewPayloadRef.current = {};
  }
  window.dispatchEvent(new CustomEvent(SESSION_CHANGE_EVENT, { detail: { token } }));
}, [token]);

useEffect(() => {
  if (!token) {
    return;
  }
  if (!isTokenValid(token)) {
    setToken(null);
  }
}, [token]);

useEffect(() => {
  if (typeof document === 'undefined') {
    return () => {};
  }
  const root = document.documentElement;
  const previousZoom = root.style.getPropertyValue('zoom');
  const previousOrigin = root.style.transformOrigin;

  if (token) {
    root.style.setProperty('zoom', '100%');
    root.style.transformOrigin = 'top center';
  } else {
    root.style.removeProperty('zoom');
    root.style.transformOrigin = '';
  }

  return () => {
    if (previousZoom) {
      root.style.setProperty('zoom', previousZoom);
    } else {
      root.style.removeProperty('zoom');
    }
    root.style.transformOrigin = previousOrigin;
  };
}, [token]);

useEffect(() => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (userKey) {
      window.localStorage.setItem(USER_STORAGE_KEY, userKey);
    } else {
      window.localStorage.removeItem(USER_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('No se pudo guardar el usuario', error);
  }
}, [userKey]);

useEffect(() => {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    if (username) {
      window.localStorage.setItem(USERNAME_STORAGE_KEY, username);
    } else {
      window.localStorage.removeItem(USERNAME_STORAGE_KEY);
    }
  } catch (error) {
    console.warn('No se pudo guardar el nombre de usuario', error);
  }
}, [username]);

  useEffect(() => {
    setVinos(loadStoredVinos(userKey));
    setUnlockedVinos(loadStoredUnlocks(userKey));
  }, [userKey, loadStoredUnlocks, loadStoredVinos]);

  const persistUnlocks = useCallback(
    (data: Record<string, boolean>) => {
      if (typeof window === 'undefined' || !userKey) {
        return;
      }
      try {
        const scopedKey = buildScopedKey(UNLOCK_STORAGE_KEY, userKey);
        if (scopedKey) {
          window.localStorage.setItem(scopedKey, JSON.stringify(data));
        }
      } catch (error) {
        console.warn('No se pudo guardar el estado de desbloqueo', error);
      }
    },
    [userKey]
  );

  const persistVinos = useCallback(
    (data: Vino[]) => {
      if (typeof window === 'undefined' || !userKey) {
        return;
      }
      try {
        const scopedKey = buildScopedKey(VINO_STORAGE_KEY, userKey);
        if (scopedKey) {
          window.localStorage.setItem(scopedKey, JSON.stringify(data));
        }
      } catch (error) {
        console.warn('No se pudo guardar el estado de vinos', error);
      }
    },
    [userKey]
  );

  useEffect(() => {
    let isMounted = true;

    const hydrateCatalogo = async () => {
      try {
        const [vinosResponse, fichasResponse] = await Promise.all([
          fetch(vinosCatalogCsv),
          fetch(fichasVinosCsv),
        ]);

        if (!vinosResponse.ok || !fichasResponse.ok) {
          throw new Error('No se pudo descargar el catálogo de vinos.');
        }

        const [vinosCsvText, fichasCsvText] = await Promise.all([
          vinosResponse.text(),
          fichasResponse.text(),
        ]);

        const catalogo = parseCatalogo(vinosCsvText);
        const fichas = parseFichas(fichasCsvText);

        if (!isMounted || catalogo.length === 0) {
          return;
        }

        setVinos((prev) => {
          const existingById = new Map(prev.map((vino) => [vino.id_vino, vino]));
          const merged = catalogo.map((vino, index) => {
            const id = index + 1;
            const existing = existingById.get(id);
            return {
              id_vino: id,
              nombre: vino.nombre,
              sku: vino.sku,
              imagenUrl: VINO_IMAGES[vino.sku] ?? null,
              ficha: fichas[vino.sku],
              puntuacion: existing?.puntuacion ?? 0,
              nota: existing?.nota ?? '',
            };
          });

          persistVinos(merged);
          return merged;
        });
      } catch (error) {
        console.warn('No se pudo cargar el CSV de vinos', error);
      }
    };

    hydrateCatalogo();

    return () => {
      isMounted = false;
    };
  }, [persistVinos]);

  useEffect(() => {
    if (vinos.length === 0) {
      return () => {};
    }

    const nearbyVinos = Array.from({ length: PRELOAD_BATCH_SIZE }, (_, offset) => {
      const index = seleccionado + offset;
      return vinos[index] ?? null;
    }).filter((vino): vino is Vino => Boolean(vino));

    nearbyVinos.forEach((vino) => {
      preloadImage(vino.imagenUrl);
    });
  }, [seleccionado, vinos]);

  const reviewTimeoutsRef = useRef<Record<number, number>>({});

  const isVinoUnlocked = useCallback(
    (vinoId: number) => unlockedVinos[String(vinoId)] === true,
    [unlockedVinos]
  );

  const handleUnlock = useCallback(() => {
    const current = vinos[seleccionado];
    if (!current) {
      return;
    }
    const key = String(current.id_vino);
    setUnlockedVinos((prev) => {
      if (prev[key]) {
        return prev;
      }
      const updated = { ...prev, [key]: true };
      persistUnlocks(updated);
      return updated;
    });
  }, [persistUnlocks, seleccionado, vinos]);

  const sendReviewUpdate = useCallback(
    (vino: Vino) => {
      if (!token) {
        return;
      }
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return;
      }

      const payload: { evento_numero: number; vino_id: number; score?: number; nota_cata?: string } = {
        evento_numero: CURRENT_EVENT_NUMBER,
        vino_id: vino.id_vino,
      };

      if (vino.puntuacion > 0) {
        payload.score = vino.puntuacion;
      }

      if (typeof vino.nota === 'string') {
        payload.nota_cata = vino.nota;
      }

      const normalizedPayload: { score?: number; nota_cata?: string } = {
        score: payload.score,
        nota_cata: payload.nota_cata,
      };

      const lastPayload = lastReviewPayloadRef.current[vino.id_vino];
      if (
        lastPayload &&
        lastPayload.score === normalizedPayload.score &&
        lastPayload.nota_cata === normalizedPayload.nota_cata
      ) {
        return;
      }

      lastReviewPayloadRef.current[vino.id_vino] = normalizedPayload;

      fetch(REVIEW_ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('La API de reseñas devolvió un estado inesperado.');
          }
        })
        .catch((error) => {
          delete lastReviewPayloadRef.current[vino.id_vino];
          console.warn('No se pudo enviar la reseña del vino', error);
        });
    },
    [token]
  );

  const scheduleReviewSync = useCallback(
    (vinoId: number, immediate = false) => {
      if (!isVinoUnlocked(vinoId)) {
        return;
      }

      if (reviewTimeoutsRef.current[vinoId]) {
        window.clearTimeout(reviewTimeoutsRef.current[vinoId]);
        delete reviewTimeoutsRef.current[vinoId];
      }

      const trigger = () => {
        const latest = vinosRef.current.find((vino) => vino.id_vino === vinoId);
        if (latest) {
          sendReviewUpdate(latest);
        }
        delete reviewTimeoutsRef.current[vinoId];
      };

      if (immediate) {
        trigger();
        return;
      }

      reviewTimeoutsRef.current[vinoId] = window.setTimeout(trigger, REVIEW_DEBOUNCE_MS);
    },
    [isVinoUnlocked, sendReviewUpdate]
  );

  // Enviar review cuando cambio de vino si hubo modificaciones
  useEffect(() => {
    const previousIndex = previousSeleccionadoRef.current;
    const previousVino = vinos[previousIndex];
    
    // Actualizar la referencia al índice actual
    previousSeleccionadoRef.current = seleccionado;
    
    // No enviar en el primer render o si no hay vino anterior
    if (previousIndex === seleccionado || !previousVino || !token) {
      return;
    }

    // Verificar si el vino anterior fue desbloqueado
    if (!isVinoUnlocked(previousVino.id_vino)) {
      return;
    }

    // Verificar si hubo cambios respecto al último payload enviado
    const currentData = {
      score: previousVino.puntuacion > 0 ? previousVino.puntuacion : undefined,
      nota_cata: typeof previousVino.nota === 'string' && previousVino.nota.trim() ? previousVino.nota : undefined,
    };

    const lastPayload = lastReviewPayloadRef.current[previousVino.id_vino];
    const hasChanges = !lastPayload || 
      lastPayload.score !== currentData.score || 
      lastPayload.nota_cata !== currentData.nota_cata;

    if (hasChanges) {
      // Cancelar cualquier timeout pendiente para este vino
      if (reviewTimeoutsRef.current[previousVino.id_vino]) {
        window.clearTimeout(reviewTimeoutsRef.current[previousVino.id_vino]);
        delete reviewTimeoutsRef.current[previousVino.id_vino];
      }
      // Enviar inmediatamente
      sendReviewUpdate(previousVino);
    }
  }, [seleccionado, vinos, token, isVinoUnlocked, sendReviewUpdate]);

  const handleCodigoChange = (value: string) => {
    const sanitized = value.replace(/\D/g, '').slice(0, 4);
    setCodigo(sanitized);
    if (codigoError) {
      setCodigoError('');
    }
    if (loginError) {
      setLoginError('');
    }
  };

  const handleLoginSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isAuthenticating) {
      return;
    }

    let hasError = false;

    if (codigo.length !== 4) {
      setCodigoError('Ingresá el código de 4 dígitos.');
      hasError = true;
    }

    if (hasError) {
      return;
    }

    setIsAuthenticating(true);
    setCodigoError('');
    setLoginError('');

    try {
      const response = await fetch(LOGIN_ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ codigo }),
      });

      const body = (await response.json().catch(() => null)) as LoginResponse | null;

      if (!response.ok) {
        const message = body && typeof body.error === 'string' ? body.error : 'No pudimos iniciar sesión. Reintentá.';
        throw new Error(message);
      }

      const receivedToken = body && typeof body.token === 'string' ? body.token : null;
      if (!receivedToken) {
        throw new Error('La respuesta del servidor no incluyó el token.');
      }

      if (explosionTimeoutRef.current !== null) {
        window.clearTimeout(explosionTimeoutRef.current);
      }

      const nextUserKey = normalizeUserKey(
        body?.user_id !== undefined ? `user-${body.user_id}` : body?.codigo || codigo
      );
      const nextUsername = typeof body?.username === 'string' ? body.username.trim() : '';
      setShowExplosion(true);
      setUserKey(nextUserKey);
      setUsername(nextUsername);
      setCodigo('');

      explosionTimeoutRef.current = window.setTimeout(() => {
        setToken(receivedToken);
        setShowExplosion(false);
        setIsAuthenticating(false);
        explosionTimeoutRef.current = null;
      }, 1200);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No pudimos iniciar sesión. Reintentá.';
      setLoginError(message);
      setIsAuthenticating(false);
      setShowExplosion(false);
      if (explosionTimeoutRef.current !== null) {
        window.clearTimeout(explosionTimeoutRef.current);
        explosionTimeoutRef.current = null;
      }
    }
  };

  const handleNotaChange = (value: string) => {
    const current = vinos[seleccionado];
    if (!current || !isVinoUnlocked(current.id_vino)) {
      return;
    }
    if (value === current.nota) {
      return;
    }
    setVinos((prev) => {
      const updated = [...prev];
      const existing = updated[seleccionado];
      if (!existing) {
        return prev;
      }
      const next = { ...existing, nota: value };
      updated[seleccionado] = next;
      persistVinos(updated);
      scheduleReviewSync(current.id_vino);
      return updated;
    });
  };

  const handlePuntuacion = (valor: number) => {
    const current = vinos[seleccionado];
    if (!current || !isVinoUnlocked(current.id_vino)) {
      return;
    }

    setVinos((prev) => {
      const updated = [...prev];
      const target = updated[seleccionado];
      if (!target) {
        return prev;
      }
      const next = {
        ...target,
        puntuacion: target.puntuacion === valor ? 0 : valor,
      };
      updated[seleccionado] = next;
      persistVinos(updated);
      scheduleReviewSync(next.id_vino, true);
      return updated;
    });
  };

  useEffect(() => () => {
    Object.values(reviewTimeoutsRef.current).forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
  }, []);

  const handleSliderPointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    const handle = sliderHandleRef.current;
    if (!handle) {
      return;
    }

    event.preventDefault();
    sliderDragRef.current.active = true;
    sliderDragRef.current.pointerId = event.pointerId;
    sliderDragRef.current.startX = event.clientX;
    sliderDragRef.current.startProgress = sliderProgress;
    selectionSourceRef.current = 'manual';

    handle.setPointerCapture(event.pointerId);
  };

  const handleSliderPointerMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!sliderDragRef.current.active) {
      return;
    }

    const track = sliderTrackRef.current;
    if (!track) {
      return;
    }

    event.preventDefault();
    const delta = event.clientX - sliderDragRef.current.startX;
    const trackWidth = Math.max(track.clientWidth, 1);
    const nextProgress = Math.min(
      Math.max(sliderDragRef.current.startProgress + delta / trackWidth, 0),
      1
    );
    const maxIndex = Math.max(vinos.length - 1, 0);
    const totalSteps = Math.max(maxIndex, 1);
    const targetIndex = Math.round(nextProgress * totalSteps);
    const clampedIndex = Math.min(Math.max(targetIndex, 0), Math.max(maxIndex, 0));
    const snappedProgress = maxIndex > 0 ? clampedIndex / maxIndex : 0;
    selectionSourceRef.current = 'manual';
    setSliderProgress(snappedProgress);
    setSeleccionado((prev) => (prev === clampedIndex ? prev : clampedIndex));
  };

  const endSliderDrag = () => {
    if (!sliderDragRef.current.active) {
      return;
    }

    const handle = sliderHandleRef.current;
    if (handle && sliderDragRef.current.pointerId !== null) {
      handle.releasePointerCapture(sliderDragRef.current.pointerId);
    }

    sliderDragRef.current.active = false;
    sliderDragRef.current.pointerId = null;
  };

  const handleSliderTrackClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const track = sliderTrackRef.current;
    const handle = sliderHandleRef.current;
    if (!track) {
      return;
    }

    if (handle && handle.contains(event.target as Node)) {
      return;
    }

    const rect = track.getBoundingClientRect();
    const progress = Math.min(
      Math.max((event.clientX - rect.left) / Math.max(rect.width, 1), 0),
      1
    );
    const maxIndex = Math.max(vinos.length - 1, 0);
    const totalSteps = Math.max(maxIndex, 1);
    const targetIndex = Math.round(progress * totalSteps);
    const clampedIndex = Math.min(Math.max(targetIndex, 0), Math.max(maxIndex, 0));
    const snappedProgress = maxIndex > 0 ? clampedIndex / maxIndex : 0;
    selectionSourceRef.current = 'manual';
    setSliderProgress(snappedProgress);
    setSeleccionado((prev) => (prev === clampedIndex ? prev : clampedIndex));
  };

  useEffect(() => {
    const container = carouselRef.current;
    if (!container) {
      return;
    }

    const evaluateSelection = () => {
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.left + containerRect.width / 2;

      let closestIndex = 0;
      let minDistance = Number.POSITIVE_INFINITY;

      itemRefs.current.forEach((item, index) => {
        if (!item) {
          return;
        }

        const itemRect = item.getBoundingClientRect();
        const itemCenter = itemRect.left + itemRect.width / 2;
        const distance = Math.abs(containerCenter - itemCenter);

        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      selectionSourceRef.current = 'auto';
      setSeleccionado((prev) => (prev === closestIndex ? prev : closestIndex));
    };

    const handleScroll = () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = window.setTimeout(() => {
        evaluateSelection();
        scrollTimeoutRef.current = null;
      }, 500);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current);
        scrollTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (vinos.length <= 1) {
      setSliderProgress(0);
    } else {
      const totalSteps = vinos.length - 1;
      const snapped = seleccionado / totalSteps;
      setSliderProgress((prev) => (prev === snapped ? prev : snapped));
    }

    if (selectionSourceRef.current !== 'manual') {
      selectionSourceRef.current = 'auto';
      return;
    }

    const container = carouselRef.current;
    const item = itemRefs.current[seleccionado];

    if (!container || !item) {
      selectionSourceRef.current = 'auto';
      return;
    }

    const containerWidth = container.clientWidth;
    const itemWidth = item.offsetWidth;
    const itemLeft = item.offsetLeft;
    const maxScrollLeft = Math.max(container.scrollWidth - containerWidth, 0);
    const targetScrollLeft = Math.min(
      Math.max(itemLeft + itemWidth / 2 - containerWidth / 2, 0),
      maxScrollLeft
    );

    container.scrollTo({
      left: targetScrollLeft,
      behavior: 'smooth',
    });

    selectionSourceRef.current = 'auto';
  }, [seleccionado, vinos.length]);

  if (!token) {
    return (
      <div className={`evento-email-gate ${showExplosion ? 'explosion-activa' : ''}`}>
        <div className="evento-video-background">
          <video
            ref={videoLoginRef}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            className="evento-background-video"
          >
            <source src={eventoVideo} type="video/mp4" />
          </video>
          <div className="evento-video-overlay" />
        </div>
        <div className={`evento-email-card ${showExplosion ? 'salida' : ''}`}>
          <h1 className="evento-email-title">Emití tu pasaporte</h1>
          <p className="evento-email-subtitle">
            <em>Un viaje inolvidable por nuestra selección única te está esperando</em>
            <br></br><br />
            El día del evento vas a poder obtener un código secreto para ingresar.
            <br />
            ¡Estate atento a nuestras redes sociales!
          </p>
          
          <div className="evento-countdown">
            <div className="countdown-display">
              <div className="countdown-unit">
                <span className="countdown-number">{countdown.days.toString().padStart(2, '0')}</span>
                <span className="countdown-label">DÍAS</span>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-unit">
                <span className="countdown-number">{countdown.hours.toString().padStart(2, '0')}</span>
                <span className="countdown-label">HRS</span>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-unit">
                <span className="countdown-number">{countdown.minutes.toString().padStart(2, '0')}</span>
                <span className="countdown-label">MIN</span>
              </div>
              <div className="countdown-separator">:</div>
              <div className="countdown-unit">
                <span className="countdown-number">{countdown.seconds.toString().padStart(2, '0')}</span>
                <span className="countdown-label">SEG</span>
              </div>
            </div>
          </div>
          <form className="evento-email-form" onSubmit={handleLoginSubmit}>
            <label htmlFor="evento-codigo-input" className="evento-email-label">
              Código de acceso
            </label>
            <input
              id="evento-codigo-input"
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={codigo}
              readOnly
              placeholder=""
              className={`evento-email-input ${codigoError ? 'con-error' : ''}`}
              required
            />
            {codigoError && <span className="evento-email-error">{codigoError}</span>}
            {loginError && <span className="evento-email-error evento-login-error">{loginError}</span>}
            <div className="evento-keypad" aria-label="Teclado de código">
              {ACCESS_KEYPAD_DIGITS.map((digit) => (
                <button
                  key={digit}
                  type="button"
                  className="evento-keypad-button"
                  onClick={() => handleCodigoChange(`${codigo}${digit}`)}
                  disabled={codigo.length >= 4 || isAuthenticating}
                  aria-label={`Agregar ${digit}`}
                >
                  {digit}
                </button>
              ))}
              <button
                type="button"
                className="evento-keypad-button evento-keypad-button--delete"
                onClick={() => handleCodigoChange(codigo.slice(0, -1))}
                disabled={codigo.length === 0 || isAuthenticating}
              >
                Borrar
              </button>
            </div>
            <button
              type="submit"
              className="evento-email-button"
              disabled={isAuthenticating}
              aria-busy={isAuthenticating}
            >
              Emitir
            </button>
          </form>
        </div>
        <div className={`evento-email-explosion ${showExplosion ? 'activa' : ''}`} aria-hidden="true" />
      </div>
    );
  }

  const sliderHandleStyle = {
    '--slider-progress': sliderProgress.toString(),
  } as React.CSSProperties;

  const detalleDesbloqueado = isVinoUnlocked(vinoActual.id_vino);
  const fichaActual = vinoActual.ficha;
  const waveColors = fichaActual ? getWaveColors(fichaActual.tipo) : getWaveColors();
  const waveStyle = detalleDesbloqueado
    ? ({
        '--evento-wave-start': waveColors.start,
        '--evento-wave-mid': waveColors.mid,
        '--evento-wave-end': waveColors.end,
      } as React.CSSProperties)
    : undefined;

  const handleOcultarTeclado = () => {
    if (notaTextareaRef.current) {
      notaTextareaRef.current.blur();
    }
    const current = vinos[seleccionado];
    if (current) {
      scheduleReviewSync(current.id_vino, true);
    }
  };

  return (
    <div className="evento-page">
      <header className="evento-header">
        <h1 className="evento-heading">Pasaporte de degustación</h1>
        {username && <p className="evento-user-name">{username}</p>}
      </header>

      <div className="evento-carousel-container">
        <div
          ref={carouselRef}
          className="evento-carousel"
          role="listbox"
          aria-label="Lista de vinos"
        >
          {vinos.map(({ id_vino, nombre, imagenUrl }, index) => {
            const desbloqueado = isVinoUnlocked(id_vino);
            const placeholderLabel = nombre.slice(0, 2).toUpperCase();

            return (
              <button
                type="button"
                key={id_vino}
                className={`evento-carousel-item ${index === seleccionado ? 'activo' : ''}`}
                onClick={() => {
                  selectionSourceRef.current = 'manual';
                  setSeleccionado(index);
                }}
                aria-label={`Seleccionar ${nombre}`}
                aria-pressed={index === seleccionado}
                ref={(element) => {
                  itemRefs.current[index] = element;
                }}
              >
                <div className={`evento-card ${desbloqueado ? 'desbloqueado' : 'bloqueado'}`}>
                  {desbloqueado ? (
                    imagenUrl ? (
                      <img
                        src={imagenUrl}
                        alt={`Etiqueta de ${nombre}`}
                        className="evento-card-imagen"
                        loading="lazy"
                      />
                    ) : (
                      <div className="evento-card-placeholder" aria-hidden="true">
                        <span>{placeholderLabel}</span>
                      </div>
                    )
                  ) : (
                    <span className="evento-card-numero-overlay" aria-hidden="true">
                      {id_vino}
                    </span>
                  )}
                </div>
                <div className="evento-card-info">
                  <span className="evento-card-numero-inline">{desbloqueado ? id_vino : '?'}</span>
                  {desbloqueado && <span className="evento-card-titulo">{nombre}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="evento-slider">
        <div
          className="evento-slider-track"
          ref={sliderTrackRef}
          onClick={handleSliderTrackClick}
        >
          <button
            type="button"
            className="evento-slider-handle"
            ref={sliderHandleRef}
            style={sliderHandleStyle}
            onPointerDown={handleSliderPointerDown}
            onPointerMove={handleSliderPointerMove}
            onPointerUp={endSliderDrag}
            onPointerCancel={endSliderDrag}
            aria-label="Deslizar para recorrer la lista de vinos"
          >
            <span className="evento-slider-grip" />
          </button>
        </div>
      </div>

      <section
        className={`evento-detalle ${detalleDesbloqueado ? '' : 'bloqueado'}`}
        aria-live="polite"
        style={waveStyle}
      >
        {detalleDesbloqueado ? (
            <>
              <div className="evento-detalle-burbujas" aria-hidden="true">
              {['izquierda', 'centro', 'derecha'].map((base) => (
                <div key={base} className={`burbuja-base ${base}`}>
                  {Array.from({ length: 6 }).map((_, index) => (
                    <span
                      key={`${base}-${index}`}
                      className={`burbuja ${base}-burbuja-${index + 1}`}
                    />
                  ))}
                </div>
              ))}
            </div>

            <header className="evento-detalle-head">
              <h2 className="evento-detalle-titulo">{vinoActual.nombre}</h2>
              <p className="evento-detalle-origen">{fichaActual?.origen || 'Origen no disponible'}</p>
              <span className="evento-detalle-numero">{vinoActual.id_vino}</span>
            </header>

            <section className="evento-ficha">
              <h3 className="evento-section-title">¿Cómo descubrirlo?</h3>
              <div className="evento-ficha-grid">
                {[
                  { label: 'Nariz', value: fichaActual?.nariz },
                  { label: 'Boca', value: fichaActual?.boca },
                  { label: '¿Cómo es?', value: fichaActual?.sensacion },
                  { label: 'Va perfecto con', value: fichaActual?.maridaje },
                ].map(({ label, value }) => (
                  <div key={label} className="evento-ficha-item">
                    <span className="evento-ficha-label">{label}:</span>
                    <span className="evento-ficha-value">{value || '—'}</span>
                  </div>
                ))}
              </div>
            </section>
            <br></br>
            <section className="evento-feedback">
              <h3 className="evento-section-title">¿Qué te pareció?</h3>
              <div className="evento-puntuacion">
                {[1, 2, 3, 4, 5].map((estrella) => (
                  <button
                    key={estrella}
                    type="button"
                    className={`evento-estrella ${vinoActual.puntuacion >= estrella ? 'llena' : ''}`}
                    onClick={() => handlePuntuacion(estrella)}
                    aria-label={`Puntuar con ${estrella} ${estrella === 1 ? 'estrella' : 'estrellas'}`}
                  >
                    <i className={vinoActual.puntuacion >= estrella ? 'fas fa-star' : 'far fa-star'} />
                  </button>
                ))}
              </div>

              <label className="evento-section-title" htmlFor="nota-degustacion">
                ¿Qué te generó?
              </label>
              <textarea
                id="nota-degustacion"
                className="evento-note-input"
                placeholder="¡Acá podes anotar tus sensaciones y opiniones sobre el vino!"
                value={vinoActual.nota}
                ref={notaTextareaRef}
                onChange={(event) => handleNotaChange(event.target.value)}
              />
              <button type="button" className="evento-note-dismiss" onClick={handleOcultarTeclado}>
                Listo
              </button>
            </section>
          </>
        ) : (
          <div className="evento-unlock-wrapper">
            <button type="button" className="evento-unlock-button" onClick={handleUnlock}>
              <span className="evento-unlock-title">Desbloquear vino</span>
              <span className="evento-unlock-subtitle">
                Tocá acá para saber más sobre este vino
              </span>
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default Evento;
